/**
 * Addons Selectors
 *
 * Memoized selectors for accessing flight addons state.
 * Using createSelector ensures components only re-render when selected data changes.
 */
import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '@/store/store';
import type { ApplyCouponData } from '@contracts';

// ============================================
// Base Selectors
// ============================================

const selectAddonsState = (state: RootState) => state.flights.addons;

// ============================================
// Addons Selectors (Memoized)
// ============================================

export const selectAddonsData = createSelector([selectAddonsState], (addons) => addons.addons.data);

export const selectAddonsLoading = createSelector(
  [selectAddonsState],
  (addons) => addons.addons.isLoading,
);

export const selectAddonsError = createSelector(
  [selectAddonsState],
  (addons) => addons.addons.error,
);

// ============================================
// Meals Selectors (Memoized)
// ============================================

export const selectMealsData = createSelector([selectAddonsState], (addons) => addons.meals.data);

export const selectMealsLoading = createSelector(
  [selectAddonsState],
  (addons) => addons.meals.isLoading,
);

export const selectMealsError = createSelector([selectAddonsState], (addons) => addons.meals.error);

/**
 * Get total count of selected meals per journey
 * Returns a record mapping journeyIndex to count of selected meals
 * This is used for per-journey meal limits (not per-sector, as the limit applies to the journey)
 */
export const selectSelectedMealsCountByJourney = createSelector([selectMealsData], (mealsData) => {
  const countByJourney: Record<number, number> = {};

  if (!mealsData?.data?.meals?.tabs) {
    return countByJourney;
  }

  for (const tab of mealsData.data.meals.tabs) {
    const journeyIndex = tab.journeyIndex ?? 0;
    const selectedCount = tab.data?.filter((item) => item.isSelected === true).length ?? 0;
    countByJourney[journeyIndex] = (countByJourney[journeyIndex] ?? 0) + selectedCount;
  }

  return countByJourney;
});

/**
 * Get total count of selected meals across all sectors/journeys
 * Counts all meal items where isSelected === true
 */
export const selectTotalSelectedMealsCount = createSelector([selectMealsData], (mealsData) => {
  if (!mealsData?.data?.meals?.tabs) {
    return 0;
  }

  let totalSelected = 0;
  for (const tab of mealsData.data.meals.tabs) {
    if (tab.data) {
      totalSelected += tab.data.filter((item) => item.isSelected === true).length;
    }
  }

  return totalSelected;
});

// ============================================
// Baggage Selectors (Memoized)
// ============================================

export const selectBaggageData = createSelector(
  [selectAddonsState],
  (addons) => addons.baggage.data,
);

export const selectBaggageLoading = createSelector(
  [selectAddonsState],
  (addons) => addons.baggage.isLoading,
);

export const selectBaggageError = createSelector(
  [selectAddonsState],
  (addons) => addons.baggage.error,
);

/**
 * Get total count of selected baggage items across all sectors/journeys
 * Counts all baggage items where isSelected === true
 */
export const selectTotalSelectedBaggageCount = createSelector(
  [selectBaggageData],
  (baggageData) => {
    if (!baggageData?.data?.baggage?.tabs) {
      return 0;
    }

    let totalSelected = 0;
    for (const tab of baggageData.data.baggage.tabs) {
      if (tab.data) {
        totalSelected += tab.data.filter((item) => item.isSelected === true).length;
      }
    }

    return totalSelected;
  },
);

// ============================================
// Coupons Selectors (Memoized)
// ============================================

export const selectCouponsData = createSelector(
  [selectAddonsState],
  (addons) => addons.coupons.data,
);

export const selectCouponsLoading = createSelector(
  [selectAddonsState],
  (addons) => addons.coupons.isLoading,
);

export const selectCouponsError = createSelector(
  [selectAddonsState],
  (addons) => addons.coupons.error,
);

// ============================================
// Seats Selectors (Memoized)
// ============================================

export const selectSeatsData = createSelector([selectAddonsState], (addons) => addons.seats.data);

export const selectSeatsLoading = createSelector(
  [selectAddonsState],
  (addons) => addons.seats.isLoading,
);

export const selectSeatsError = createSelector([selectAddonsState], (addons) => addons.seats.error);
/**
 * Get the currently selected coupon from Redux state
 * Returns the coupon object if one is selected, null otherwise
 */
export const selectSelectedCoupon = createSelector([selectCouponsData], (couponsData) => {
  if (!couponsData?.coupons?.coupons) {
    return null;
  }

  const selectedCoupon = couponsData.coupons.coupons.find((coupon) => coupon.isSelected === true);
  return selectedCoupon ?? null;
});

/**
 * Get the applied coupon data from Redux state (for popup display)
 * Returns the ApplyCouponData if a coupon was applied, null otherwise
 */
export const selectAppliedCouponData = createSelector(
  [selectAddonsState],
  (addons): ApplyCouponData | null => addons.coupons.appliedCouponData,
);

/**
 * Get total count of selected addons per journey
 * Returns a record mapping journeyIndex to count of selected addons
 * Used for per-journey addon limits (each journey has its own selection limit)
 */
export const selectSelectedAddonsCountByJourney = createSelector(
  [selectAddonsData],
  (addonsData) => {
    const countByJourney: Record<number, number> = {};

    if (!addonsData?.data?.addons?.tabs) {
      return countByJourney;
    }

    for (const tab of addonsData.data.addons.tabs) {
      const journeyIndex = tab.journeyIndex ?? 0;
      const selectedCount = tab.data?.filter((item) => item.isSelected === true).length ?? 0;
      countByJourney[journeyIndex] = (countByJourney[journeyIndex] ?? 0) + selectedCount;
    }

    return countByJourney;
  },
);

/**
 * Get total count of selected addon items across all sectors/journeys
 * Counts all addon items where isSelected === true
 */
export const selectTotalSelectedAddonsCount = createSelector([selectAddonsData], (addonsData) => {
  if (!addonsData?.data?.addons?.tabs) {
    return 0;
  }

  let totalSelected = 0;
  for (const tab of addonsData.data.addons.tabs) {
    if (tab.data) {
      totalSelected += tab.data.filter((item) => item.isSelected === true).length;
    }
  }

  return totalSelected;
});
